import {
  ref,
  provide,
  inject,
  readonly,
  onMounted,
  onUnmounted,
  watch,
  type ComputedRef,
  type Ref,
  type InjectionKey,
  computed,
  type TemplateRef,
} from "vue";

const HeaderHeightKey: InjectionKey<{
  headerHeight: Readonly<Ref<number>>;
  headerHeightPixel: ComputedRef<string>;
}> = Symbol("HeaderHeight");

export function useHeaderHeight() {
  const existingContext = inject(HeaderHeightKey, null);

  if (existingContext) {
    return {
      ...existingContext,
      initHeaderObserver: () => {
        // eslint-disable-next-line no-console
        console.warn(
          "initHeaderObserver should only be called from the layout component that provides the context",
        );
        return { stopObserving: () => {} };
      },
    };
  }

  const headerHeight = ref(0);
  const headerHeightPixel = computed(() => `${headerHeight.value}px`);
  let resizeObserver: ResizeObserver | null = null;
  let isObserving = false;

  const updateHeaderHeight = (height: number) => {
    headerHeight.value = height;
  };

  const initHeaderObserver = (
    headerElementRef: TemplateRef<HTMLElement | undefined>,
  ) => {
    const startObserving = () => {
      const element = headerElementRef.value;
      if (!element || isObserving) return;

      updateHeaderHeight(element.offsetHeight);

      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            updateHeaderHeight(entry.contentRect.height);
          }
        });

        resizeObserver.observe(element);
        isObserving = true;
      }
    };

    const stopObserving = () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
        isObserving = false;
      }
    };

    const stopWatcher = watch(
      () => headerElementRef.value,
      (newElement) => {
        stopObserving();
        if (newElement) {
          startObserving();
        }
      },
      { immediate: true },
    );

    onUnmounted(() => {
      stopObserving();
      stopWatcher();
    });

    onMounted(() => {
      startObserving();
    });

    return {
      stopObserving,
    };
  };

  provide(HeaderHeightKey, {
    headerHeight: readonly(headerHeight),
    headerHeightPixel,
  });

  return {
    headerHeight: readonly(headerHeight),
    headerHeightPixel,
    initHeaderObserver,
  };
}
